import { Component, Fragment, ReactNode, RefObject, createRef } from "react";
import DOMPurify from "dompurify";
import { EmailAttachmentType } from "../../DataTypes/mail";

// Fallback for messages that only have a plain-text body (no body_html):
// wrap URL substrings we detect ourselves in an <a>, everything else stays
// plain text that React escapes normally.
const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<>"')\]]+)/gi
const isUrl = (part: string) => /^(?:https?:\/\/|www\.)[^\s<>"')\]]+$/i.test(part)

export const linkifyText = (text: string): ReactNode => {
    if (!text) return null
    const parts = text.split(URL_PATTERN)
    return parts.map((part, i) => {
        if (!isUrl(part)) return <Fragment key={i}>{part}</Fragment>
        const href = part.startsWith('www.') ? `https://${part}` : part
        return (
            <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="mail-body-link">
                {part}
            </a>
        )
    })
}

export const getInitial = (address: string) => (address || '?').trim().charAt(0).toUpperCase()

export const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIndex = 0
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
    }
    return `${size.toFixed(unitIndex > 0 && size < 10 ? 1 : 0)} ${units[unitIndex]}`
}

// Every link in sanitized email HTML opens safely in a new tab — without
// rel="noopener noreferrer" a target="_blank" link can reverse-tabnab this app.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
    }
})

// HTML emails commonly reference inline images as <img src="cid:...">, where
// the id matches an attachment's Content-ID header. Swap those for the real
// attachment URL (+ the auth token, since <img> can't send a header) before
// sanitizing, otherwise every inline image just shows as broken.
const CID_PATTERN = /cid:([^"'()\s]+)/gi
const stripAngleBrackets = (value: string) => value.replace(/^<|>$/g, '')

const resolveInlineImages = (html: string, attachments: EmailAttachmentType[], token: string | null): string => {
    if (!attachments.length) return html
    return html.replace(CID_PATTERN, (match, rawCid) => {
        const cid = stripAngleBrackets(rawCid)
        const attachment = attachments.find(a => a.content_id && stripAngleBrackets(a.content_id) === cid)
        if (!attachment) return match
        return token ? `${attachment.url}?token=${token}` : attachment.url
    })
}

// Some emails' own boilerplate CSS sets html/body to e.g. height:100% or
// overflow:hidden, sizing the page relative to the iframe's *current*
// viewport rather than its actual content — that fights MailHtmlFrame's
// measurement (and can feed back into it, since resizing the iframe changes
// what 100% means). Force those two properties off after the email's own
// styles, so scrollHeight always reflects true content height.
const IFRAME_SIZE_OVERRIDE = '<style>html,body{height:auto!important;min-height:0!important;overflow:visible!important;margin:0!important;}</style>'

// Untrusted HTML (a synced email, or anything a user could have pasted into
// a signature field) — never render it without sanitizing first.
// WHOLE_DOCUMENT keeps any <style> block intact (real HTML emails lean on it
// heavily, using tag/class selectors for the whole layout) — that's normally
// dangerous since a <style> tag isn't scoped to wherever it's injected and
// would leak out and reskin the rest of the app, but MailHtmlFrame renders
// this inside a sandboxed <iframe> with its own document, so it's fully
// contained no matter what the content's CSS targets.
export const sanitizeEmailHtml = (html: string, attachments: EmailAttachmentType[] = [], token: string | null = null): string => {
    const withResolvedImages = resolveInlineImages(html, attachments, token)
    const sanitized = DOMPurify.sanitize(withResolvedImages, { WHOLE_DOCUMENT: true })
    return sanitized.includes('</head>')
        ? sanitized.replace('</head>', `${IFRAME_SIZE_OVERRIDE}</head>`)
        : `${IFRAME_SIZE_OVERRIDE}${sanitized}`
}

export type SignatureFields = {
    display_name?: string;
    position?: string;
    email_address?: string;
    phone?: string;
}

// Mirrors SharedEmailSignature.render() server-side, for a live preview.
// unsubscribe_url is per-send/per-recipient on the backend — '#' stands in
// for it since there's no specific send to generate a real one from here.
export const renderSignaturePreview = (template: string, fields: SignatureFields): string => {
    return template
        .replace(/\{\{lead_manager_name\}\}/g, fields.display_name || '')
        .replace(/\{\{lead_manager_position\}\}/g, fields.position || '')
        .replace(/\{\{lead_manager_email\}\}/g, fields.email_address || '')
        .replace(/\{\{lead_manager_phone\}\}/g, fields.phone || '')
        .replace(/\{\{unsubscribe_url\}\}/g, '#')
}

type MailHtmlFrameProps = {
    html: string;
    className?: string;
}

// Renders sanitized HTML in total isolation from the app: its own document,
// so its own <style> block/colors/fonts render exactly as designed instead
// of fighting our global CSS (or leaking out and affecting the rest of the
// app). sandbox="allow-same-origin allow-popups" with no allow-scripts token
// means: no JavaScript can execute inside it at all (the real security
// boundary — DOMPurify is the second layer, not the only one),
// allow-same-origin just lets this component measure the rendered height so
// the iframe isn't a fixed/scrolling box, and allow-popups lets the
// target="_blank" links we force onto every <a> open.
export class MailHtmlFrame extends Component<MailHtmlFrameProps> {
    iframeRef: RefObject<HTMLIFrameElement | null> = createRef()
    resizeObserver: ResizeObserver | null = null
    measureFrame: number | null = null

    componentWillUnmount(): void {
        this.resizeObserver?.disconnect()
        if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame)
    }

    // Setting iframe.style.height synchronously from inside the ResizeObserver
    // callback resizes the very element (the iframe's own child document)
    // that's being observed — the browser can't finish delivering the
    // current notification batch before another one is queued, and Chrome
    // surfaces that as a (harmless, but CRA's overlay treats it as a crash)
    // "ResizeObserver loop completed with undelivered notifications" error.
    // Deferring the write to the next frame breaks that same-tick loop, and
    // skipping no-op writes cuts out most of the redundant churn too.
    measure = () => {
        if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame)
        this.measureFrame = requestAnimationFrame(() => {
            this.measureFrame = null
            const iframe = this.iframeRef.current
            const doc = iframe?.contentWindow?.document
            if (!iframe || !doc) return
            const height = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0, 40)
            const next = `${height}px`
            if (iframe.style.height !== next) {
                iframe.style.height = next
            }
        })
    }

    handleLoad = () => {
        this.measure()

        // Keep tracking size after the load event — images finishing a beat
        // late, web fonts swapping in and reflowing text, etc. would
        // otherwise leave the iframe a few px short and trigger its own
        // internal scrollbar.
        const doc = this.iframeRef.current?.contentWindow?.document
        if (!doc?.body) return
        this.resizeObserver?.disconnect()
        this.resizeObserver = new ResizeObserver(this.measure)
        this.resizeObserver.observe(doc.body)
    }

    render(): ReactNode {
        return (
            <iframe
                ref={this.iframeRef}
                className={`mail-html-frame ${this.props.className || ''}`}
                srcDoc={this.props.html}
                sandbox="allow-same-origin allow-popups"
                onLoad={this.handleLoad}
                title="HTML content"
            />
        )
    }
}
